// Library Methods
import { useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import {
  ChevronDownIcon,
  SingleColumnIcon,
  TwoColumnsIcon,
  ThreeColumnsIcon,
  FourColumnsIcon,
  TwoRowsIcon,
  ThreeRowsIcon,
  TwoByTwoGridIcon,
  ThreeByTwoGridIcon,
  TwoByThreeGridIcon,
  TwoByFourGridIcon,
  ThreeByThreeGridIcon,
  ThreeByFourGridIcon,
  SplitRowsThirdIcon,
  SplitRowsQuarterIcon,
  SplitGridThirdIcon,
  SplitGridQuarterIcon,
  TwoPlusOneIcon,
  TwoPlusOneVerticalIcon,
  ThreePlusOneIcon,
  ThreePlusOneVerticalIcon,
  OneVertPlusTwoIcon,
  OneVertPlusFourIcon,
  OneVertPlusSixIcon,
  OneVertPlusNineIcon,
} from "src/assets/icons";

// Styles
import {
  getSurfaceTokens,
  getPopoverPaperSx,
} from "src/styles/mui/muiStylesHelper";

// One explicitly-placed cell within a "spanning" layout's repeating block,
// in the block's own 1-based local row numbering (offset by the block's
// position in the overall grid when actually rendered).
type SpanningCellTemplate = {
  gridColumn: string;
  rowStart: number;
  rowSpan: number;
  // Overrides the default 300px image height for this specific cell (e.g.
  // so a spanning row can be a different fraction of the block's total
  // height than the row(s) above/below it).
  imageHeight?: string;
};

// "columns" just flows cards into that many even columns (wrapping into as
// many rows as needed). "grouped" chunks cards into fixed `rows x columns`
// blocks, all cards the same size, with a bigger gap between each block.
// "asymmetric" is like "grouped" but each row within a block gets its own
// pane height (e.g. a small 1/3-height row above a large 2/3-height row),
// repeating every `rowImageHeights.length` rows. "spanning" is for blocks
// where a cell needs to explicitly span multiple columns and/or rows (e.g.
// one large pane next to two stacked smaller ones), which plain per-row
// heights can't express.
export type GridLayoutValue =
  | { mode: "columns"; columns: number }
  | { mode: "grouped"; rows: number; columns: number }
  | {
      mode: "asymmetric";
      id: string;
      columns: number;
      rowImageHeights: string[];
    }
  | {
      mode: "spanning";
      id: string;
      columns: number;
      rowsPerBlock: number;
      cells: SpanningCellTemplate[];
    };

export const isSameGridLayout = (a: GridLayoutValue, b: GridLayoutValue) => {
  if (a.mode !== b.mode) return false;
  if (a.mode === "columns" && b.mode === "columns") {
    return a.columns === b.columns;
  }
  if (a.mode === "grouped" && b.mode === "grouped") {
    return a.rows === b.rows && a.columns === b.columns;
  }
  if (a.mode === "asymmetric" && b.mode === "asymmetric") {
    return a.id === b.id;
  }
  if (a.mode === "spanning" && b.mode === "spanning") {
    return a.id === b.id;
  }
  return true;
};

type LayoutOption = {
  id: string;
  value: GridLayoutValue;
  icon: JSX.Element;
  label: string;
};

// A tall main pane spanning the full height of a `thumbnailRows`-tall
// block, next to a `thumbnailColumns x thumbnailRows` grid of thumbnails
// (e.g. a 1x2 strip, or a proper 2x2/2x3 grid once there are more than a
// couple of thumbnails). The main pane always takes up half the total
// width, however many thumbnail columns there are.
//
// Thumbnails are left at the default 300px card height (same as every
// other layout, including 2+1 Vert/3+1 Vert) rather than an explicit
// smaller override -- the main pane's height is entirely dictated by its
// row(s), so an explicit override here would make the "large image" a
// different size across otherwise-equivalent layouts.
const createOneVertPlusGrid = (
  thumbnailColumns: number,
  thumbnailRows: number,
  icon: JSX.Element
): LayoutOption => {
  const totalThumbnails = thumbnailColumns * thumbnailRows;
  const id = `spanning-1vert-plus-${totalThumbnails}`;

  const thumbnailCells: SpanningCellTemplate[] = [];
  for (let row = 1; row <= thumbnailRows; row++) {
    for (let col = 1; col <= thumbnailColumns; col++) {
      thumbnailCells.push({
        gridColumn: `${thumbnailColumns + col} / ${thumbnailColumns + col + 1}`,
        rowStart: row,
        rowSpan: 1,
      });
    }
  }

  return {
    id,
    value: {
      mode: "spanning",
      id,
      columns: thumbnailColumns * 2,
      rowsPerBlock: thumbnailRows,
      cells: [
        {
          gridColumn: `1 / ${thumbnailColumns + 1}`,
          rowStart: 1,
          rowSpan: thumbnailRows,
        },
        ...thumbnailCells,
      ],
    },
    icon,
    label: `1 Vert + ${totalThumbnails}`,
  };
};

const oneVertPlusTwoOption = createOneVertPlusGrid(
  1,
  2,
  <OneVertPlusTwoIcon />
);

// Ordered by row count first, then column count, so the list reads as a
// steady progression (1x1, 1x2, ... 2x1, 2x2, ... 3x1, 3x2, ...).
const SYMMETRICAL_LAYOUT_OPTIONS: LayoutOption[] = [
  // 1 row
  {
    id: "1",
    value: { mode: "columns", columns: 1 },
    icon: <SingleColumnIcon />,
    label: "1 × 1",
  },
  {
    id: "2",
    value: { mode: "columns", columns: 2 },
    icon: <TwoColumnsIcon />,
    label: "1 × 2",
  },
  {
    id: "3",
    value: { mode: "columns", columns: 3 },
    icon: <ThreeColumnsIcon />,
    label: "1 × 3",
  },
  {
    id: "4",
    value: { mode: "columns", columns: 4 },
    icon: <FourColumnsIcon />,
    label: "1 × 4",
  },
  // 2 rows
  {
    id: "grouped-2x1",
    value: { mode: "grouped", rows: 2, columns: 1 },
    icon: <TwoRowsIcon />,
    label: "2 × 1",
  },
  {
    id: "grouped-2x2",
    value: { mode: "grouped", rows: 2, columns: 2 },
    icon: <TwoByTwoGridIcon />,
    label: "2 × 2",
  },
  {
    id: "grouped-2x3",
    value: { mode: "grouped", rows: 2, columns: 3 },
    icon: <TwoByThreeGridIcon />,
    label: "2 × 3",
  },
  {
    id: "grouped-2x4",
    value: { mode: "grouped", rows: 2, columns: 4 },
    icon: <TwoByFourGridIcon />,
    label: "2 × 4",
  },
  // 3 rows
  {
    id: "grouped-3x1",
    value: { mode: "grouped", rows: 3, columns: 1 },
    icon: <ThreeRowsIcon />,
    label: "3 × 1",
  },
  {
    id: "grouped-3x2",
    value: { mode: "grouped", rows: 3, columns: 2 },
    icon: <ThreeByTwoGridIcon />,
    label: "3 × 2",
  },
  {
    id: "grouped-3x3",
    value: { mode: "grouped", rows: 3, columns: 3 },
    icon: <ThreeByThreeGridIcon />,
    label: "3 × 3",
  },
  {
    id: "grouped-3x4",
    value: { mode: "grouped", rows: 3, columns: 4 },
    icon: <ThreeByFourGridIcon />,
    label: "3 × 4",
  },
];

// Uneven-pane layouts -- a "small" row/pane sized as a fraction of a
// "large" one, instead of every card being the same size.
const ASYMMETRICAL_LAYOUT_OPTIONS: LayoutOption[] = [
  {
    id: "asymmetric-2x1-third",
    value: {
      mode: "asymmetric",
      id: "asymmetric-2x1-third",
      columns: 1,
      rowImageHeights: ["300px", "150px"],
    },
    icon: <SplitRowsThirdIcon />,
    label: "2 × 1 (⅓)",
  },
  {
    id: "asymmetric-2x1-quarter",
    value: {
      mode: "asymmetric",
      id: "asymmetric-2x1-quarter",
      columns: 1,
      rowImageHeights: ["300px", "100px"],
    },
    icon: <SplitRowsQuarterIcon />,
    label: "2 × 1 (¼)",
  },
  {
    id: "asymmetric-2x2-third",
    value: {
      mode: "asymmetric",
      id: "asymmetric-2x2-third",
      columns: 2,
      rowImageHeights: ["300px", "150px"],
    },
    icon: <SplitGridThirdIcon />,
    label: "2 × 2 (⅓)",
  },
  {
    id: "asymmetric-2x2-quarter",
    value: {
      mode: "asymmetric",
      id: "asymmetric-2x2-quarter",
      columns: 2,
      rowImageHeights: ["300px", "100px"],
    },
    icon: <SplitGridQuarterIcon />,
    label: "2 × 2 (¼)",
  },
  {
    id: "spanning-2plus1",
    value: {
      mode: "spanning",
      id: "spanning-2plus1",
      columns: 2,
      rowsPerBlock: 2,
      cells: [
        { gridColumn: "1 / 2", rowStart: 1, rowSpan: 1, imageHeight: "300px" },
        { gridColumn: "2 / 3", rowStart: 1, rowSpan: 1, imageHeight: "300px" },
        { gridColumn: "1 / 3", rowStart: 2, rowSpan: 1, imageHeight: "150px" },
      ],
    },
    icon: <TwoPlusOneIcon />,
    label: "2 + 1",
  },
  {
    id: "spanning-2plus1-vert",
    value: {
      mode: "spanning",
      id: "spanning-2plus1-vert",
      columns: 2,
      rowsPerBlock: 2,
      cells: [
        { gridColumn: "1 / 2", rowStart: 1, rowSpan: 1 },
        { gridColumn: "1 / 2", rowStart: 2, rowSpan: 1 },
        { gridColumn: "2 / 3", rowStart: 1, rowSpan: 2 },
      ],
    },
    icon: <TwoPlusOneVerticalIcon />,
    label: "2 + 1 Vert",
  },
  {
    id: "spanning-3plus1",
    value: {
      mode: "spanning",
      id: "spanning-3plus1",
      columns: 3,
      rowsPerBlock: 2,
      cells: [
        { gridColumn: "1 / 2", rowStart: 1, rowSpan: 1, imageHeight: "300px" },
        { gridColumn: "2 / 3", rowStart: 1, rowSpan: 1, imageHeight: "300px" },
        { gridColumn: "3 / 4", rowStart: 1, rowSpan: 1, imageHeight: "300px" },
        { gridColumn: "1 / 4", rowStart: 2, rowSpan: 1, imageHeight: "150px" },
      ],
    },
    icon: <ThreePlusOneIcon />,
    label: "3 + 1",
  },
  {
    id: "spanning-3plus1-vert",
    value: {
      mode: "spanning",
      id: "spanning-3plus1-vert",
      columns: 2,
      rowsPerBlock: 3,
      cells: [
        { gridColumn: "1 / 2", rowStart: 1, rowSpan: 1 },
        { gridColumn: "1 / 2", rowStart: 2, rowSpan: 1 },
        { gridColumn: "1 / 2", rowStart: 3, rowSpan: 1 },
        { gridColumn: "2 / 3", rowStart: 1, rowSpan: 3 },
      ],
    },
    icon: <ThreePlusOneVerticalIcon />,
    label: "3 + 1 Vert",
  },
  // A single 1-wide strip of thumbnails, but 2x2/2x3 grids once there are
  // more than a couple -- otherwise a single narrow column of many small
  // thumbnails gets cramped fast.
  oneVertPlusTwoOption,
  createOneVertPlusGrid(2, 2, <OneVertPlusFourIcon />),
  createOneVertPlusGrid(2, 3, <OneVertPlusSixIcon />),
  createOneVertPlusGrid(3, 3, <OneVertPlusNineIcon />),
];

const ALL_LAYOUT_OPTIONS = [
  ...SYMMETRICAL_LAYOUT_OPTIONS,
  ...ASYMMETRICAL_LAYOUT_OPTIONS,
];

// The largest asymmetrical layouts
export const LARGE_SPANNING_LAYOUT_IDS = new Set([
  "spanning-1vert-plus-4",
  "spanning-1vert-plus-6",
  "spanning-1vert-plus-9",
]);

// The largest symmetrical layouts
export const HIDDEN_SYMMETRICAL_LAYOUT_IDS = new Set([
  "4",
  "grouped-2x4",
  "grouped-3x4",
]);

// The smallest vert layout
export const SMALL_SCREEN_SPANNING_FALLBACK = oneVertPlusTwoOption.value;

export const DEFAULT_GRID_LAYOUT: GridLayoutValue = {
  mode: "columns",
  columns: 4,
};

type GridLayoutPopoverType = {
  value: GridLayoutValue;
  onChange: (value: GridLayoutValue) => void;
};

const GridLayoutPopover = ({ value, onChange }: GridLayoutPopoverType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { surface, onSurface, mutedText, dividerBorder } =
    getSurfaceTokens(theme);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const selectedOption = ALL_LAYOUT_OPTIONS.find((option) =>
    isSameGridLayout(option.value, value)
  );

  const visibleSymmetricalOptions = isSmallScreen
    ? SYMMETRICAL_LAYOUT_OPTIONS.filter(
        (option) => !HIDDEN_SYMMETRICAL_LAYOUT_IDS.has(option.id)
      )
    : SYMMETRICAL_LAYOUT_OPTIONS;

  const visibleAsymmetricalOptions = isSmallScreen
    ? ASYMMETRICAL_LAYOUT_OPTIONS.filter(
        (option) => !LARGE_SPANNING_LAYOUT_IDS.has(option.id)
      )
    : ASYMMETRICAL_LAYOUT_OPTIONS;

  const handleSelect = (layout: GridLayoutValue) => {
    onChange(layout);
    setAnchorEl(null);
  };

  // `perRow` forces a fixed number of columns (e.g. so labels always line
  // up 4 to a row) instead of just wrapping freely at the container's width.
  const renderOptionGroup = (options: LayoutOption[], perRow?: number) => (
    <Box
      sx={
        perRow
          ? {
              display: "grid",
              gridTemplateColumns: `repeat(${perRow}, 1fr)`,
              gap: "8px",
              mt: "8px",
            }
          : {
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              mt: "8px",
            }
      }
    >
      {options.map((option) => {
        const isSelected = isSameGridLayout(option.value, value);

        return (
          <Box
            key={option.id}
            id={`comparison-grid-layout-option-${option.id}`}
            onClick={() => handleSelect(option.value)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              minWidth: "56px",
              flexShrink: 0,
              padding: "6px 8px",
              borderRadius: "4px",
              border: isSelected ? "1px solid #E2772E" : dividerBorder,
              backgroundColor: isSelected ? "#E2772E20" : surface,
              color: isSelected ? "#E2772E" : onSurface,
              cursor: "pointer",
            }}
          >
            {option.icon}
            <Typography fontSize="10px" fontWeight={600} whiteSpace="nowrap">
              {option.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  return (
    <>
      <Box
        id="comparison-grid-layout-trigger"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          border: dividerBorder,
          borderRadius: "4px",
          padding: "6px 8px",
          cursor: "pointer",
          color: onSurface,
          backgroundColor: isOpen ? "#5B6D7A20" : "transparent",
        }}
      >
        <Box sx={{ display: "flex" }}>{selectedOption?.icon}</Box>
        <Typography fontSize="12px" fontWeight={600}>
          {selectedOption?.label}
        </Typography>
        <ChevronDownIcon
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </Box>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: getPopoverPaperSx(theme),
        }}
      >
        <Box sx={{ width: "340px", p: "16px" }}>
          <Typography
            fontSize="12px"
            fontWeight={600}
            sx={{
              color: mutedText,
              textTransform: "uppercase",
            }}
          >
            {t("Symmetrical Layouts")}
          </Typography>

          {renderOptionGroup(visibleSymmetricalOptions, 4)}

          <Typography
            fontSize="12px"
            fontWeight={600}
            sx={{
              color: mutedText,
              textTransform: "uppercase",
              mt: "16px",
            }}
          >
            {t("Asymmetrical Layouts")}
          </Typography>

          {renderOptionGroup(visibleAsymmetricalOptions, 4)}
        </Box>
      </Popover>
    </>
  );
};

export default GridLayoutPopover;
